/**
 * StructuredPlan — plan clínico estructurado, presentado como renglones de un
 * documento (no como tarjetas ni tablas de formulario).
 *
 * Cada recurso es una línea tipográfica: chip de tipo + nombre + detalles.
 * Todo se edita al hacer clic (EditableText); las acciones destructivas solo
 * aparecen al pasar el mouse. La categorización inteligente y el parseo de
 * medicamentos se conservan intactos — solo cambia la presentación.
 *
 * Secciones: Diagnósticos · Tratamiento · Órdenes · Educación · Seguimiento
 */

import { useMemo, useCallback } from 'react';
import { Trash2, Plus, AlertTriangle } from 'lucide-react';
import type { FHIRItem } from '../../services/medicalAI';
import { EditableText } from './EditableText';
import styles from './StructuredPlan.module.css';

/* ── Public props ── */

type MedField = 'display' | 'presentation' | 'dose' | 'route' | 'frequency' | 'duration';

interface StructuredPlanProps {
  fhir: FHIRItem[];
  healthEducation: string;
  planText: string;           // soap.p — used to extract follow-up
  /** Curación del plan (persiste sobre la base IA). Si se omiten, es de solo lectura. */
  onRemove?: (id: string) => void;
  onEditMed?: (id: string, patch: Partial<Pick<FHIRItem, MedField>>) => void;
  onAddMed?: (item: FHIRItem) => void;
  /** Edición de la educación al paciente (persiste en la nota). */
  onEditEducacion?: (texto: string) => void;
}

/* ── Smart re-categorisation ── */

type PlanCategory = 'diagnosis' | 'medication' | 'order' | 'education' | 'other';

/* Las listas de términos se comparan con FRONTERA DE PALABRA. Sin ella, las
 * siglas cortas coincidían dentro de palabras comunes y un DIAGNÓSTICO
 * terminaba clasificado como orden: "vías respiratorias **alt**as" activaba
 * `alt` (la enzima), "G**ast**ritis" activaba `ast`, "a**pet**ito" activaba
 * `pet` (PET scan) y "3 **ves**…" activaba `ves`.
 *
 * Se usan lookarounds en vez de `\b` porque `\b` se apoya en [A-Za-z0-9_] y
 * fallaría con términos acentuados: en "ácido úrico" no hay frontera antes de
 * la `á`, así que `\bácido` nunca coincidiría. */
const conFrontera = (alternativas: string) =>
  new RegExp(`(?<![\\p{L}\\p{N}])(?:${alternativas})(?![\\p{L}\\p{N}])`, 'iu');

const LAB_KEYWORDS = conFrontera('hemograma|orina|glicemia|glucosa|creatinina|bun|electrolitos|hemoglobina|hematocrito|plaquetas|leucocitos|linfocitos|biometría|perfil|lipídico|tsh|t3|t4|vih|vdrl|hepatitis|cultivo|urocultivo|pcr|procalcitonina|dímero|troponina|bnp|amilasa|lipasa|bilirrubina|transaminasa|alt|ast|fosfatasa|ggt|albúmina|proteínas|coagulograma|tp|tpt|inr|fibrinógeno|eritrosedimentación|ves|ferritina|hierro|vitamina|ácido\\s*úrico|calcio|fósforo|magnesio|sodio|potasio|cloro');

const IMAGING_KEYWORDS = conFrontera('placa|radiografía|rayos\\s*x|rx|tomografía|tac|tc|resonancia|rmn|rm|ecografía|ultrasonido|us|mamografía|densitometría|gammagrafía|pet|angiografía|fluoroscopia|ecocardiograma');

const MED_KEYWORDS = conFrontera('paracetamol|acetaminofén|ibuprofeno|diclofenaco|naproxeno|aspirina|amoxicilina|azitromicina|cefalexina|ciprofloxacina|metformina|losartán|enalapril|amlodipino|atorvastatina|omeprazol|pantoprazol|salbutamol|fluticasona|loratadina|cetirizina|prednisona|dexametasona|insulina|levotiroxina|mucosol|compuesto|jarabe|tableta|cápsula|comprimido|ampolla|solución|suspensión|gotas|crema|ungüento|mg|ml');

const REFERRAL_KEYWORDS = conFrontera('interconsulta|referencia|derivación|especialista|cardiología|neurología|gastroenterología|endocrinología|nefrología|neumología|dermatología|otorrinolaringología|oftalmología|urología|ginecología|traumatología|psiquiatría|psicología');

/** ICD-10 codes: letter + 2 digits, optionally .digits */
const ICD10_PATTERN = /^[A-Z]\d{2}(\.\d{1,2})?$/i;
/** LOINC codes: numeric with optional dash */
const LOINC_PATTERN = /^\d{3,5}-\d$/;
/** RxNorm/ATC: purely numeric or alphanumeric medication codes */
const RXNORM_PATTERN = /^\d{4,6}$/;

function smartCategorise(item: FHIRItem): PlanCategory {
  // Trust the AI type when it's not 'condition' (properly classified)
  if (item.type === 'medication') return 'medication';
  if (item.type === 'labOrder' || item.type === 'labResult') return 'order';
  if (item.type === 'imagingOrder') return 'order';
  if (item.type === 'referral') return 'order';
  if (item.type === 'procedure') return 'order';
  if (item.type === 'allergy' || item.type === 'familyHistory' || item.type === 'personalHistory') return 'other';

  // For 'condition' type — check if the AI mis-categorised
  const text = `${item.display} ${item.details ?? ''}`;
  const code = item.code ?? '';

  // Code-based detection
  if (LOINC_PATTERN.test(code)) return 'order';
  if (RXNORM_PATTERN.test(code)) return 'medication';
  if (ICD10_PATTERN.test(code)) return 'diagnosis';

  // Keyword-based detection (order matters: most specific first)
  if (MED_KEYWORDS.test(text)) return 'medication';
  if (LAB_KEYWORDS.test(text)) return 'order';
  if (IMAGING_KEYWORDS.test(text)) return 'order';
  if (REFERRAL_KEYWORDS.test(text)) return 'order';

  // Default: if it has an ICD-like code or no code, treat as diagnosis
  return 'diagnosis';
}

/* ── Order sub-type for display ── */

type OrderSubType = 'lab' | 'imaging' | 'referral' | 'procedure';

function detectOrderSubType(item: FHIRItem): OrderSubType {
  if (item.type === 'labOrder' || item.type === 'labResult') return 'lab';
  if (item.type === 'imagingOrder') return 'imaging';
  if (item.type === 'referral') return 'referral';
  if (item.type === 'procedure') return 'procedure';
  const text = `${item.display} ${item.details ?? ''}`;
  if (IMAGING_KEYWORDS.test(text)) return 'imaging';
  if (REFERRAL_KEYWORDS.test(text)) return 'referral';
  return 'lab';
}

const ORDER_LABELS: Record<OrderSubType, string> = {
  lab: 'Lab',
  imaging: 'Imagen',
  referral: 'Referido',
  procedure: 'Proc',
};

/* ── Follow-up extraction ── */

function extractFollowUp(planText: string): string | null {
  if (!planText) return null;
  const patterns = [
    /(?:seguimiento|control|pr[oó]xima\s*cita|regresar|volver)[:\s]*([^.;\n]{5,120})/i,
    /(?:en\s+\d+\s+(?:d[ií]as|semanas|meses))[^.;\n]{0,60}/i,
    /(?:cita\s*(?:de\s*)?(?:control|seguimiento)[^.;\n]{0,80})/i,
  ];
  for (const regex of patterns) {
    const match = planText.match(regex);
    if (match) return match[0].trim();
  }
  return null;
}

/* ── Medication detail parser ── */

interface MedDetail {
  name: string;
  presentation: string | null;   // tableta, cápsula, jarabe...
  dose: string | null;           // 500mg
  route: string | null;          // oral, IV, IM...
  frequency: string | null;      // cada 8h
  duration: string | null;       // por 7 días
  prescriptionType?: FHIRItem['prescriptionType']; // M9: formal | recomendacion | reportado_paciente
}

const PRESENTATION_RE = /\b(tabletas?|cápsulas?|comprimidos?|jarabes?|solución|suspensión|gotas|crema|ungüento|ampolla|inyectable|supositorio|parche|inhalador|spray|sobres?)\b/i;
const DOSE_RE = /(\d+(?:[.,]\d+)?\s*(?:mg|g|ml|mcg|ui|%|mg\/ml|mg\/5ml))/i;
const ROUTE_RE = /\b(oral|v[ií]a\s*oral|iv|im|sc|sublingual|tópic[oa]|inhalatoria|rectal|nasal|oftálmic[oa]|ót?ic[oa])\b/i;
const FREQUENCY_RE = /(cada\s*\d+\s*(?:h(?:oras?)?|hrs?)|(?:\d+\s*veces?\s*al\s*d[ií]a)|(?:tid|bid|qd|qid|prn|sos)|c\/\d+h)/i;
const DURATION_RE = /((?:por|durante|x)\s*\d+\s*(?:d[ií]as?|semanas?|meses?)|\d+\s*d[ií]as?)/i;

function parseMedDetails(item: FHIRItem): MedDetail {
  const fullText = `${item.display} ${item.dose ?? ''} ${item.details ?? ''}`;
  // Prefer structured fields from AI, fall back to regex extraction from free text
  return {
    name: item.display,
    presentation: item.presentation ?? fullText.match(PRESENTATION_RE)?.[1] ?? null,
    dose: item.dose ?? fullText.match(DOSE_RE)?.[1] ?? null,
    route: item.route ?? fullText.match(ROUTE_RE)?.[1] ?? null,
    frequency: item.frequency ?? fullText.match(FREQUENCY_RE)?.[1] ?? null,
    duration: item.duration ?? fullText.match(DURATION_RE)?.[1] ?? null,
    prescriptionType: item.prescriptionType,
  };
}

/** Campos de posología (todos existen en MedDetail, a diferencia de `display`). */
type CampoPosologia = Extract<MedField, 'presentation' | 'dose' | 'route' | 'frequency' | 'duration'>;

/** Campos de un medicamento en el orden en que se leen en la receta. */
const CAMPOS_MED: Array<{ campo: CampoPosologia; ph: string }> = [
  { campo: 'presentation', ph: 'presentación' },
  { campo: 'dose',         ph: 'dosis' },
  { campo: 'route',        ph: 'vía' },
  { campo: 'frequency',    ph: 'frecuencia' },
  { campo: 'duration',     ph: 'duración' },
];

/* ── Component ── */

export function StructuredPlan({
  fhir, healthEducation, planText, onRemove, onEditMed, onAddMed, onEditEducacion,
}: StructuredPlanProps) {
  const editable = !!onEditMed;

  const handleEdit = useCallback(
    (id: string, field: MedField, value: string) => {
      onEditMed?.(id, { [field]: value || undefined });
    },
    [onEditMed],
  );

  const handleAddMed = useCallback(() => {
    onAddMed?.({ id: `med_manual_${Date.now().toString(36)}`, type: 'medication', status: 'active', display: '' });
  }, [onAddMed]);

  const handleAddOrden = useCallback(() => {
    onAddMed?.({ id: `lab_manual_${Date.now().toString(36)}`, type: 'labOrder', status: 'active', display: '' });
  }, [onAddMed]);

  const handleAddDx = useCallback(() => {
    onAddMed?.({ id: `dx_manual_${Date.now().toString(36)}`, type: 'condition', status: 'active', display: '' });
  }, [onAddMed]);

  const categorised = useMemo(() => {
    const diagnoses: FHIRItem[] = [];
    const medications: FHIRItem[] = [];
    const orders: FHIRItem[] = [];

    for (const item of fhir) {
      // Skip allergy / history — shown elsewhere
      if (item.type === 'allergy' || item.type === 'familyHistory' || item.type === 'personalHistory') continue;

      const cat = smartCategorise(item);
      if (cat === 'diagnosis') diagnoses.push(item);
      else if (cat === 'medication') medications.push(item);
      else if (cat === 'order') orders.push(item);
    }

    return { diagnoses, medications, orders };
  }, [fhir]);

  const followUp = useMemo(() => extractFollowUp(planText), [planText]);

  /** Medicamentos a los que les falta algún dato de posología, con el detalle
   *  de QUÉ falta: una receta sin dosis o sin vía no se puede despachar. */
  const medsIncompletos = useMemo(() => {
    const ETIQUETA: Record<CampoPosologia, string> = {
      presentation: 'presentación', dose: 'dosis', route: 'vía',
      frequency: 'frecuencia', duration: 'duración',
    };
    return categorised.medications
      .map((med) => {
        const d = parseMedDetails(med);
        const faltan = CAMPOS_MED.filter(({ campo }) => !d[campo]).map(({ campo }) => ETIQUETA[campo]);
        return { nombre: d.name || 'medicamento sin nombre', faltan };
      })
      .filter((m) => m.faltan.length > 0);
  }, [categorised.medications]);

  /** Botón de eliminar que solo se ve al pasar el mouse sobre el renglón. */
  const botonEliminar = (item: FHIRItem, etiqueta: string) =>
    onRemove ? (
      <button
        type="button"
        className={styles.accionQuitar}
        onClick={() => onRemove(item.id)}
        title={`Quitar ${etiqueta}`}
        aria-label={`Quitar ${etiqueta}: ${item.display || 'sin nombre'}`}
      >
        <Trash2 size={12} aria-hidden="true" />
      </button>
    ) : null;

  const enlaceAgregar = (onClick: () => void, texto: string) =>
    onAddMed ? (
      <button type="button" className={styles.enlaceAgregar} onClick={onClick}>
        <Plus size={11} aria-hidden="true" /> {texto}
      </button>
    ) : null;

  return (
    <div className={styles.root}>

      {/* Corte entre la nota narrativa (S·O·A·P·E) y los recursos
          estructurados: son dos naturalezas distintas — prosa clínica arriba,
          tarjetas codificadas abajo — y la nota se lee mejor si se ven como
          dos bloques y no como una lista continua de secciones. */}
      <div className={styles.corte}>
        <span className={styles.corteEtiqueta}>Plan estructurado</span>
      </div>

      {/* ── Diagnósticos ── */}
      <section className={styles.sec}>
        <span className={styles.insignia} aria-hidden="true">Dx</span>
        <div className={styles.cuerpo}>
          <header className={styles.encabezado}>
            <h4 className={styles.titulo}>Diagnósticos</h4>
            <span className={styles.hint}>codificados CIE-10</span>
          </header>
          {categorised.diagnoses.length === 0 ? (
            <p className={styles.vacio}>Aparecerán cuando el médico establezca el diagnóstico.</p>
          ) : (
            <ul className={styles.lista}>
              {categorised.diagnoses.map((dx) => (
                <li key={dx.id} className={styles.renglon}>
                  <span className={styles.tipo}>Dx</span>
                  <span className={styles.contenido}>
                    <EditableText
                      variant="inline"
                      className={styles.nombre}
                      value={dx.display}
                      placeholder="nombre del diagnóstico"
                      onCommit={(v) => handleEdit(dx.id, 'display', v)}
                      disabled={!editable}
                      ariaLabel="Nombre del diagnóstico"
                    />
                    {dx.code && <span className={styles.codigo}>{dx.code}</span>}
                    {dx.details && dx.details !== dx.display && (
                      <span className={styles.detalle}>{dx.details}</span>
                    )}
                  </span>
                  {botonEliminar(dx, 'diagnóstico')}
                </li>
              ))}
            </ul>
          )}
          {enlaceAgregar(handleAddDx, 'Agregar diagnóstico')}
        </div>
      </section>

      {/* ── Tratamiento ── */}
      <section className={styles.sec}>
        <span className={styles.insignia} aria-hidden="true">Rx</span>
        <div className={styles.cuerpo}>
          <header className={styles.encabezado}>
            <h4 className={styles.titulo}>Tratamiento</h4>
            <span className={styles.hint}>medicamentos prescritos</span>
          </header>
          {categorised.medications.length === 0 ? (
            <p className={styles.vacio}>Los medicamentos que prescriba aparecerán aquí.</p>
          ) : (
            <ul className={styles.lista}>
              {categorised.medications.map((med) => {
                const d = parseMedDetails(med);
                return (
                  <li key={med.id} className={`${styles.renglon} ${styles.renglonMed}`}>
                    <span className={styles.tipo}>Med</span>
                    <span className={styles.contenido}>
                      <span className={styles.lineaNombre}>
                        <EditableText
                          variant="inline"
                          className={styles.nombre}
                          value={d.name}
                          placeholder="nombre del medicamento"
                          onCommit={(v) => handleEdit(med.id, 'display', v)}
                          disabled={!editable}
                          ariaLabel="Nombre del medicamento"
                        />
                        {d.prescriptionType === 'recomendacion' && (
                          <span className={`${styles.etiqueta} ${styles.etiquetaRec}`}>recomendación</span>
                        )}
                        {d.prescriptionType === 'reportado_paciente' && (
                          <span className={`${styles.etiqueta} ${styles.etiquetaPac}`}>refiere el paciente</span>
                        )}
                      </span>
                      <span className={styles.posologia}>
                        {CAMPOS_MED.map(({ campo, ph }, i) => (
                          <span key={campo} className={styles.campo}>
                            {i > 0 && <span className={styles.separador} aria-hidden="true">·</span>}
                            <EditableText
                              variant="inline"
                              className={d[campo] ? styles.dato : styles.datoFalta}
                              value={d[campo] ?? ''}
                              placeholder={ph}
                              onCommit={(v) => handleEdit(med.id, campo, v)}
                              disabled={!editable}
                              ariaLabel={`${ph} de ${d.name || 'el medicamento'}`}
                            />
                          </span>
                        ))}
                      </span>
                      {med.warning && (
                        <span className={styles.advertencia}>{med.warning}</span>
                      )}
                    </span>
                    {botonEliminar(med, 'medicamento')}
                  </li>
                );
              })}
            </ul>
          )}
          {/* Aviso de posología incompleta. Los huecos ya se ven en cada
              renglón, pero sin este resumen es fácil firmar una receta a la que
              le falta la dosis o la vía: se nombra el medicamento y qué falta. */}
          {medsIncompletos.length > 0 && (
            <p className={styles.avisoIncompletos}>
              <AlertTriangle size={12} aria-hidden="true" />
              <span>
                {medsIncompletos.length === 1 ? 'Falta completar: ' : 'Faltan datos en: '}
                {medsIncompletos.map((m, i) => (
                  <span key={m.nombre}>
                    {i > 0 && '; '}
                    <strong>{m.nombre}</strong> ({m.faltan.join(', ')})
                  </span>
                ))}
                {/* En una nota ya firmada el aviso sigue siendo útil como
                    constancia, pero pedir que se complete no tiene sentido:
                    ahí solo caben notas de observación. */}
                {onEditMed ? '. Complételos o confírmelos antes de finalizar.' : '.'}
              </span>
            </p>
          )}
          {enlaceAgregar(handleAddMed, 'Agregar medicamento')}
        </div>
      </section>

      {/* ── Órdenes ── */}
      <section className={styles.sec}>
        <span className={styles.insignia} aria-hidden="true">Or</span>
        <div className={styles.cuerpo}>
          <header className={styles.encabezado}>
            <h4 className={styles.titulo}>Órdenes y referidos</h4>
            <span className={styles.hint}>laboratorio · imagen · interconsulta</span>
          </header>
          {categorised.orders.length === 0 ? (
            <p className={styles.vacio}>Sin órdenes por el momento.</p>
          ) : (
            <ul className={styles.lista}>
              {categorised.orders.map((ord) => (
                <li key={ord.id} className={styles.renglon}>
                  <span className={styles.tipo}>{ORDER_LABELS[detectOrderSubType(ord)]}</span>
                  <span className={styles.contenido}>
                    <EditableText
                      variant="inline"
                      className={styles.nombre}
                      value={ord.display}
                      placeholder="nombre del estudio"
                      onCommit={(v) => handleEdit(ord.id, 'display', v)}
                      disabled={!editable}
                      ariaLabel="Nombre del estudio"
                    />
                    {ord.code && <span className={styles.codigo}>{ord.code}</span>}
                    {ord.details && ord.details !== ord.display && (
                      <span className={styles.detalle}>{ord.details}</span>
                    )}
                  </span>
                  {botonEliminar(ord, 'orden')}
                </li>
              ))}
            </ul>
          )}
          {enlaceAgregar(handleAddOrden, 'Agregar orden')}
        </div>
      </section>

      {/* ── Educación al paciente ── */}
      <section className={styles.sec}>
        <span className={styles.insignia} aria-hidden="true">Ed</span>
        <div className={styles.cuerpo}>
          <header className={styles.encabezado}>
            <h4 className={styles.titulo}>Educación al paciente</h4>
            <span className={styles.hint}>visible en la app del paciente</span>
          </header>
          <EditableText
            value={healthEducation}
            placeholder="Las recomendaciones para el paciente aparecerán aquí."
            onCommit={(v) => onEditEducacion?.(v)}
            disabled={!onEditEducacion}
            ariaLabel="Educación al paciente"
          />
        </div>
      </section>

      {/* ── Seguimiento (derivado del Plan, solo lectura) ── */}
      {followUp && (
        <section className={styles.sec}>
          <span className={styles.insignia} aria-hidden="true">Sg</span>
          <div className={styles.cuerpo}>
            <header className={styles.encabezado}>
              <h4 className={styles.titulo}>Seguimiento</h4>
              <span className={styles.hint}>detectado en el Plan</span>
            </header>
            <p className={styles.seguimiento}>{followUp}</p>
          </div>
        </section>
      )}
    </div>
  );
}
